import { can } from "../../auth/can.ts";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import {
  setDefaultSort,
  setFetch,
  setPage,
  setSortBy,
} from "../../store/slices/directBossSlice.ts";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import moment from "moment";
import "moment/locale/es";
import {
  deleteDirectBoss,
  DirectBoss,
} from "../../services/direct-boss.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import ModalContainer from "../ModalContainer.tsx";
import DirectBossForm from "./DirectBossForm.tsx";
import BossProfileAvatar from "./BossProfileAvatar.tsx";
import EmployeeInitialsAvatars from "./EmployeeInitialsAvatars.tsx";
import {
  directBossPageStyles as s,
  DIRECT_BOSS_PAGE_KEYFRAMES,
} from "./directBossPageStyles.ts";

moment.locale("es");

const TableDirectBoss = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { directBosses, sortBy, defaultSort } = useSelector(
    (state: RootState) => state.directBoss
  );
  const dispatch = useDispatch();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingDirectBoss, setEditingDirectBoss] = useState<DirectBoss | null>(
    null
  );
  const [deletingDirectBoss, setDeletingDirectBoss] =
    useState<DirectBoss | null>(null);
  const [loading, setLoading] = useState(false);

  const getIconSort = () => (
    <i
      className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"} ms-2`}
      style={{ fontSize: 11, opacity: 0.7 }}
    />
  );

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";
    dispatch(setSortBy(sort));
    dispatch(setPage(0));
    dispatch(setDefaultSort(sortOrder));
  };

  const handleEdit = (directBoss: DirectBoss) => {
    setEditingDirectBoss(directBoss);
    setShowEditModal(true);
  };

  const handleDelete = (directBoss: DirectBoss) => {
    setDeletingDirectBoss(directBoss);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDirectBoss) return;

    try {
      setLoading(true);
      await deleteDirectBoss(deletingDirectBoss.id);
      notification("Éxito", "Jefe directo eliminado correctamente", "success");
      dispatch(setFetch(true));
      setShowDeleteModal(false);
      setDeletingDirectBoss(null);
    } catch (error) {
      console.error("Error deleting direct boss:", error);
      notification("Error", "No se pudo eliminar el jefe directo", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    dispatch(setFetch(true));
    setShowEditModal(false);
    setEditingDirectBoss(null);
  };

  const getBossName = (boss: DirectBoss["boss"]) => {
    if (!boss?.person) return "N/A";
    return toCamelCase(
      `${boss.person.firstName} ${boss.person.lastName} ${
        boss.person.secondLastName || ""
      }`
    );
  };

  const formatDate = (date: string) =>
    moment(date).format("D [de] MMMM [de] YYYY");

  const isAdmin = can("direct_boss.manage", user);
  const rows =
    directBosses && Array.isArray(directBosses) ? directBosses : [];
  const colSpan = isAdmin ? 4 : 3;

  return (
    <>
      <style>{DIRECT_BOSS_PAGE_KEYFRAMES}</style>

      <div style={s.tableWrapper}>
        <div style={s.tableScroll}>
          <table style={s.table} className="table table-hover mb-0">
            <thead style={s.thead}>
              <tr>
                <th
                  style={{ ...s.th, width: "32%" }}
                  onClick={() => setSort("boss")}
                >
                  <span className="d-inline-flex align-items-center">
                    Jefe directo
                    {sortBy === "boss" && getIconSort()}
                  </span>
                </th>
                <th style={{ ...s.thStatic, width: "38%" }}>Equipo</th>
                <th
                  style={{ ...s.th, width: "22%" }}
                  onClick={() => setSort("createdAt")}
                >
                  <span className="d-inline-flex align-items-center">
                    Registro
                    {sortBy === "createdAt" && getIconSort()}
                  </span>
                </th>
                {isAdmin && (
                  <th
                    style={{ ...s.thStatic, width: "8%", textAlign: "center" }}
                  >
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((item) => {
                  const teamCount = item.employees?.length || 0;
                  return (
                    <tr
                      key={item.id}
                      style={s.trHover}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={s.td}>
                        <div style={s.bossCell}>
                          <BossProfileAvatar
                            partnerId={item.boss?.id}
                            curp={item.boss?.person?.curp}
                            firstName={item.boss?.person?.firstName}
                            lastName={item.boss?.person?.lastName}
                            size={44}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div style={s.bossName}>{getBossName(item.boss)}</div>
                            <div style={s.bossRole}>Jefe directo</div>
                          </div>
                        </div>
                      </td>
                      <td style={s.td}>
                        {teamCount === 0 ? (
                          <span
                            style={{
                              ...s.metaValue,
                              fontStyle: "italic",
                              color: "#94a3b8",
                            }}
                          >
                            Sin empleados asignados
                          </span>
                        ) : (
                          <EmployeeInitialsAvatars
                            employees={item.employees || []}
                          />
                        )}
                      </td>
                      <td style={s.td}>
                        <span style={s.metaValue}>
                          {formatDate(item.createdAt)}
                        </span>
                      </td>
                      {isAdmin && (
                        <td style={{ ...s.td, textAlign: "center" }}>
                          <div
                            style={{
                              ...s.actions,
                              marginLeft: 0,
                              justifyContent: "center",
                            }}
                          >
                            <button
                              type="button"
                              style={s.actionBtn("edit")}
                              onClick={() => handleEdit(item)}
                              disabled={loading}
                              title="Editar jefe directo"
                            >
                              <i
                                className="fa fa-edit"
                                style={{ fontSize: 14 }}
                              />
                            </button>
                            <button
                              type="button"
                              style={s.actionBtn("delete")}
                              onClick={() => handleDelete(item)}
                              disabled={loading}
                              title="Eliminar jefe directo"
                            >
                              <i
                                className="fa fa-trash"
                                style={{ fontSize: 14 }}
                              />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={colSpan} style={{ border: "none" }}>
                    <div style={s.emptyState}>
                      <div
                        style={{
                          width: 72,
                          height: 72,
                          borderRadius: "50%",
                          backgroundColor: "#e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 1rem",
                        }}
                      >
                        <i
                          className="fa fa-users"
                          style={{ fontSize: 28, color: "#64748b" }}
                        />
                      </div>
                      <h5
                        style={{
                          color: "#334155",
                          marginBottom: 8,
                          fontWeight: 600,
                        }}
                      >
                        No hay jefes directos registrados
                      </h5>
                      <p style={{ color: "#64748b", margin: 0, fontSize: 14 }}>
                        Comienza creando tu primer jefe directo
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModalContainer
        open={showEditModal}
        closeFn={() => {
          setShowEditModal(false);
          setEditingDirectBoss(null);
        }}
        title="Editar Jefe Directo"
        size="lg"
      >
        <DirectBossForm
          close={() => {
            setShowEditModal(false);
            setEditingDirectBoss(null);
          }}
          onSuccess={handleEditSuccess}
          directBossToEdit={editingDirectBoss}
          isEditing={true}
        />
      </ModalContainer>

      <ModalContainer
        open={showDeleteModal}
        closeFn={() => {
          setShowDeleteModal(false);
          setDeletingDirectBoss(null);
        }}
        title="Eliminar Jefe Directo"
        size="sm"
      >
        <div className="p-4">
          <div className="alert alert-warning mb-3" role="alert">
            <i className="fa fa-exclamation-triangle me-2" />
            <strong>¿Estás seguro?</strong>
          </div>
          <p className="mb-3">
            Se eliminará permanentemente el jefe directo{" "}
            <strong>
              {deletingDirectBoss?.boss?.person?.firstName}{" "}
              {deletingDirectBoss?.boss?.person?.lastName}
            </strong>
            . Esta acción no se puede deshacer.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingDirectBoss(null);
              }}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin me-1" />
                  Eliminando...
                </>
              ) : (
                <>
                  <i className="fa fa-trash me-1" />
                  Eliminar
                </>
              )}
            </button>
          </div>
        </div>
      </ModalContainer>
    </>
  );
};

export default TableDirectBoss;
